import { useState, useEffect, useCallback } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatters';
import { toast } from 'sonner';
import {
  ListTodo, AlertTriangle, Flag, Wrench, ChevronUp, ChevronDown, CheckCircle2, Plus, Loader2,
} from 'lucide-react';
import axios from 'axios';

import { API } from '../../utils/api';

const PRIORITY_CONFIG = {
  High: { color: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertTriangle },
  Low: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Flag },
  Maintenance: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Wrench }
};

const TaskRow = ({ task, isCompleted }) => {
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Low;
  const PriorityIcon = priorityConfig.icon;
  const isOverdue = !isCompleted && task.date_needed && new Date(task.date_needed) < new Date();

  return (
    <TableRow key={task.id} className={`border-b border-slate-100 ${isCompleted ? 'opacity-60' : ''}`} data-testid={`task-row-${task.id}`}>
      <TableCell className="py-2">
        <Badge variant="outline" className={`${priorityConfig.color} text-xs`}>
          <PriorityIcon className="w-3 h-3 mr-1" />
          {task.priority}
        </Badge>
      </TableCell>
      <TableCell className="text-sm py-2">{task.assigned_to_name}</TableCell>
      <TableCell className="text-sm text-slate-600 py-2">{task.created_by_name}</TableCell>
      <TableCell className="text-sm py-2">{task.task_description}</TableCell>
      <TableCell className="text-sm text-slate-600 py-2 whitespace-nowrap">{formatDate(task.created_at)}</TableCell>
      <TableCell className={`text-sm py-2 whitespace-nowrap ${isOverdue ? 'text-rose-600 font-semibold' : ''}`}>
        {formatDate(task.date_needed)}
      </TableCell>
      {isCompleted && (
        <TableCell className="text-sm py-2 whitespace-nowrap text-emerald-600">
          {formatDate(task.date_completed)}
        </TableCell>
      )}
    </TableRow>
  );
};

const MobileTaskCard = ({ task, isCompleted, expandedId, onToggle }) => {
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Low;
  const PriorityIcon = priorityConfig.icon;
  const isExpanded = expandedId === task.id;
  const isOverdue = !isCompleted && task.date_needed && new Date(task.date_needed) < new Date();

  return (
    <div key={task.id} className={isCompleted ? 'opacity-60' : ''}>
      <div
        className="px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => onToggle(isExpanded ? null : task.id)}
        data-testid={`mobile-task-row-${task.id}`}
      >
        <div className="grid grid-cols-12 gap-1 items-center">
          <div className="col-span-3">
            <Badge variant="outline" className={`${priorityConfig.color} text-xs`}>
              <PriorityIcon className="w-3 h-3 mr-1" />
              {task.priority}
            </Badge>
          </div>
          <div className="col-span-8 min-w-0">
            <p className={`text-sm truncate ${isCompleted ? 'line-through text-slate-500' : 'text-slate-800'}`}>{task.task_description}</p>
            <p className="text-xs text-slate-500">Assigned to: {task.assigned_to_name}</p>
          </div>
          <div className="col-span-1 flex justify-end">
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 bg-slate-50/50 border-t border-slate-100">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Full Task</span>
              <span className="text-slate-800 text-right max-w-[200px]">{task.task_description}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Assigned By</span>
              <span className="text-slate-700">{task.created_by_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Created</span>
              <span className="text-slate-700">{formatDate(task.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Date Needed</span>
              <span className={isOverdue ? 'text-rose-600 font-semibold' : 'text-slate-700'}>
                {formatDate(task.date_needed)}
              </span>
            </div>
            {isCompleted && (
              <div className="flex justify-between">
                <span className="text-slate-500">Completed</span>
                <span className="text-emerald-600 font-medium">{formatDate(task.date_completed)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PRIORITY_OPTIONS = [
  { value: 'High', label: 'High', icon: AlertTriangle },
  { value: 'Low', label: 'Low', icon: Flag },
  { value: 'Maintenance', label: 'Maintenance', icon: Wrench },
];

const EMPTY_TASK_FORM = {
  assigned_to_id: '',
  task_description: '',
  date_needed: '',
  priority: 'Low',
};

export const TasksTab = ({ llcTasks, llcId, getAuthHeaders, onTaskCreated }) => {
  const [expandedMobileTask, setExpandedMobileTask] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  // Create task state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [assignees, setAssignees] = useState([]);
  const [taskFormLoading, setTaskFormLoading] = useState(false);

  const fetchAllTasks = useCallback(async () => {
    if (!llcId || !getAuthHeaders) return;
    setLoadingAll(true);
    try {
      const res = await axios.get(`${API}/tasks/llc/${llcId}?include_completed=true`, { headers: getAuthHeaders() });
      setAllTasks(res.data);
    } catch {
      setAllTasks([]);
    } finally {
      setLoadingAll(false);
    }
  }, [llcId, getAuthHeaders]);

  useEffect(() => {
    if (showCompleted && allTasks.length === 0) {
      fetchAllTasks();
    }
  }, [showCompleted, fetchAllTasks, allTasks.length]);

  // Fetch assignees when dialog opens
  useEffect(() => {
    if (createDialogOpen && assignees.length === 0) {
      axios.get(`${API}/tasks/assignees`, { headers: getAuthHeaders() })
        .then(res => setAssignees(res.data))
        .catch(() => toast.error('Failed to load assignees'));
    }
  }, [createDialogOpen, getAuthHeaders, assignees.length]);

  const handleCreateTask = async () => {
    if (!taskForm.assigned_to_id || !taskForm.task_description || !taskForm.date_needed) {
      toast.error('Please fill in all required fields');
      return;
    }
    setTaskFormLoading(true);
    try {
      await axios.post(`${API}/tasks`, {
        llc_id: llcId,
        assigned_to_id: taskForm.assigned_to_id,
        task_description: taskForm.task_description,
        date_needed: taskForm.date_needed,
        priority: taskForm.priority,
      }, { headers: getAuthHeaders() });
      toast.success('Task created successfully');
      setCreateDialogOpen(false);
      setTaskForm(EMPTY_TASK_FORM);
      if (onTaskCreated) onTaskCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setTaskFormLoading(false);
    }
  };

  const displayTasks = showCompleted ? allTasks : llcTasks;
  const openTasks = showCompleted ? allTasks.filter(t => !t.date_completed) : llcTasks;
  const completedTasks = showCompleted ? allTasks.filter(t => t.date_completed) : [];

  return (
    <TabsContent value="open-tasks" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif flex items-center gap-2">
                <ListTodo className="w-5 h-5" />
                {showCompleted ? 'All Tasks' : 'Open Tasks'}
              </CardTitle>
              <CardDescription>
                {showCompleted
                  ? `${openTasks.length} open, ${completedTasks.length} completed`
                  : `${llcTasks.length} open task${llcTasks.length !== 1 ? 's' : ''} for this LLC`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3" data-testid="tasks-header-actions">
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="create-task-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Create Task
              </Button>
              <div className="flex items-center gap-2" data-testid="show-completed-toggle">
                <Switch
                  id="show-completed"
                  checked={showCompleted}
                  onCheckedChange={setShowCompleted}
                />
                <Label htmlFor="show-completed" className="text-sm text-slate-600 cursor-pointer">
                  Show Completed
                </Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loadingAll && showCompleted ? (
            <div className="text-center py-6 text-slate-500 text-sm">Loading tasks...</div>
          ) : displayTasks.length === 0 ? (
            <div className="text-center py-8">
              <ListTodo className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <h3 className="font-serif text-lg font-semibold text-slate-700">No tasks</h3>
              <p className="text-slate-500 mt-1">{showCompleted ? 'No tasks found for this LLC' : 'All tasks for this LLC are complete'}</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden">
                <div className="bg-slate-800 rounded-t-lg px-3 py-2.5">
                  <div className="grid grid-cols-12 gap-1 text-white text-xs font-semibold">
                    <div className="col-span-3">Priority</div>
                    <div className="col-span-9">Task</div>
                  </div>
                </div>
                <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {openTasks.map(task => (
                    <MobileTaskCard key={task.id} task={task} isCompleted={false} expandedId={expandedMobileTask} onToggle={setExpandedMobileTask} />
                  ))}
                  {showCompleted && completedTasks.length > 0 && (
                    <>
                      <div className="px-3 py-2 bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Completed ({completedTasks.length})
                      </div>
                      {completedTasks.map(task => (
                        <MobileTaskCard key={task.id} task={task} isCompleted={true} expandedId={expandedMobileTask} onToggle={setExpandedMobileTask} />
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px] py-2">Priority</TableHead>
                      <TableHead className="py-2">Assigned To</TableHead>
                      <TableHead className="py-2">Assigned By</TableHead>
                      <TableHead className="py-2">Task</TableHead>
                      <TableHead className="py-2 whitespace-nowrap">Created</TableHead>
                      <TableHead className="py-2 whitespace-nowrap">Date Needed</TableHead>
                      {showCompleted && <TableHead className="py-2 whitespace-nowrap">Completed</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openTasks.map(task => (
                      <TaskRow key={task.id} task={task} isCompleted={false} />
                    ))}
                    {showCompleted && completedTasks.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell colSpan={7} className="py-2 bg-emerald-50">
                            <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Completed ({completedTasks.length})
                            </div>
                          </TableCell>
                        </TableRow>
                        {completedTasks.map(task => (
                          <TaskRow key={task.id} task={task} isCompleted={true} />
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) setTaskForm(EMPTY_TASK_FORM); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-serif">Create Task</DialogTitle>
            <DialogDescription>Add a new task for this LLC</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Assign To <span className="text-rose-500">*</span></Label>
              <Select value={taskForm.assigned_to_id} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to_id: v })}>
                <SelectTrigger data-testid="create-task-assignee-select">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {assignees.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                <SelectTrigger data-testid="create-task-priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Task Description <span className="text-rose-500">*</span></Label>
              <Input
                value={taskForm.task_description}
                onChange={(e) => setTaskForm({ ...taskForm, task_description: e.target.value })}
                placeholder="Enter task description"
                data-testid="create-task-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Date Needed <span className="text-rose-500">*</span></Label>
              <Input
                type="date"
                value={taskForm.date_needed}
                onChange={(e) => setTaskForm({ ...taskForm, date_needed: e.target.value })}
                data-testid="create-task-date-needed-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateTask}
              disabled={taskFormLoading || !taskForm.assigned_to_id || !taskForm.task_description || !taskForm.date_needed}
              data-testid="create-task-submit-btn"
              className="bg-amber-500 hover:bg-amber-600"
            >
              {taskFormLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};
